'use client';

import { useQuery } from '@tanstack/react-query';

import { useCustomAddressStore, useStore } from '@libs/common-next';
import type { DeliveryMethodType } from '@libs/common-types';

import { fetchMiniPdpProductTailored } from '../api';

const ONE_MINUTE = 60 * 1000;

export const MINI_PDP_TAILORED_QUERY_KEY = 'mini-pdp-product-tailored';

type UseQueryMiniPdpTailoredProps = {
    variantSkus: string[];
    deliveryMethodsList?: DeliveryMethodType[];
};

export const useQueryMiniPdpTailored = ({ deliveryMethodsList, variantSkus }: UseQueryMiniPdpTailoredProps) => {
    const { selectedStoreId } = useStore();
    const sddAddress = useCustomAddressStore(state => state.address);
    const hasAddressStateHydrated = useCustomAddressStore(state => state._hasHydrated);

    const queryKey = [
        MINI_PDP_TAILORED_QUERY_KEY,
        selectedStoreId,
        ...(deliveryMethodsList || []),
        variantSkus,
        sddAddress,
    ].filter(Boolean);

    const { data, isLoading, isPending, isFetching, isError, error } = useQuery({
        queryKey,
        queryFn: () => {
            if (!deliveryMethodsList) return;

            return fetchMiniPdpProductTailored({
                deliveryMethodsList,
                storeId: selectedStoreId,
                customAddress: sddAddress ?? undefined,
                variantSkus,
            });
        },
        enabled: hasAddressStateHydrated && Boolean(deliveryMethodsList?.length),
        staleTime: ONE_MINUTE,
        retry: false,
    });

    return { data, isLoading: isLoading || !hasAddressStateHydrated, isPending, isFetching, isError, error };
};
